// File: src/components/Header.jsx
// Updated with complete admin navigation including dropdowns

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ShoppingCart, User, LogOut, Menu, X } from 'lucide-react';
import { categoriesAPI, cartAPI } from '../services/api';
import { useUser } from '../contexts/UserContext';
import SearchBar from './SearchBar';
import AdminNotifications from './AdminNotifications';
import './css/Header.css';

const Header = ({ openCart, isCartOpen, closeCart, isAdminPage }) => {
  const { user, logout, notifications, loadingNotifications, markAllAsRead, clearSection } = useUser();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Admin dropdowns state
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showCategoriesAdminDropdown, setShowCategoriesAdminDropdown] = useState(false);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [showOrdersDropdown, setShowOrdersDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await categoriesAPI.getAll();
        setCategories(data);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchCartCount = async () => {
      try {
        const { data } = await cartAPI.getCart();
        const count = data.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(count);
      } catch (err) {
        console.error('Failed to fetch cart count:', err);
      }
    };
    fetchCartCount();
  }, []);

  useEffect(() => {
    if (isCartOpen) {
      const timer = setTimeout(() => {
        const fetchCartCount = async () => {
          try {
            const { data } = await cartAPI.getCart();
            const count = data.reduce((sum, item) => sum + item.quantity, 0);
            setCartCount(count);
          } catch (err) {
            console.error('Failed to fetch cart count:', err);
          }
        };
        fetchCartCount();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isCartOpen]);

  const toggleCategoriesDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCategoriesDropdown(prev => !prev);
  };

  const handleCategoriesTextClick = (e) => {
    e.stopPropagation();
    navigate('/categories');
    setShowCategoriesDropdown(false);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.categories-dropdown') &&
        !event.target.closest('.categories-trigger') &&
        !event.target.closest('.categories-text') &&
        !event.target.closest('.admin-dropdown-wrapper')
      ) {
        setShowCategoriesDropdown(false);
        setShowProductsDropdown(false);
        setShowCategoriesAdminDropdown(false);
        setShowUsersDropdown(false);
        setShowOrdersDropdown(false);
        setShowSettingsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAllAdminDropdowns = () => {
    setShowProductsDropdown(false);
    setShowCategoriesAdminDropdown(false);
    setShowUsersDropdown(false);
    setShowOrdersDropdown(false);
    setShowSettingsDropdown(false);
  };

  const handleAdminNavClick = () => {
    closeAllAdminDropdowns();
    setMobileMenuOpen(false);
  };

  // Admin Header - Fully Responsive with Dropdowns
  if (user?.isAdmin) {
    return (
      <nav className="admin-nav">
        <div className="admin-container">
          <div className="admin-top-row">
            <div className="admin-brand">
              <img
                src="https://res.cloudinary.com/drj2hrug3/image/upload/v1761774280/Picsart_25-10-29_22-42-31-059_kuvztc.png"
                alt="ReyZa"
                className="admin-logo"
                decoding="async"
              />
              <span className="admin-badge">ADMIN</span>
            </div>
            
            <div className="admin-actions-mobile">
              <AdminNotifications
                notifications={notifications}
                loadingNotifications={loadingNotifications}
                onMarkAllRead={markAllAsRead}
                onClearSection={clearSection}
              />
              
              <button 
                className="mobile-menu-toggle admin-mobile-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Basculer le menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          <div className={`admin-links-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <div className="admin-links-container">
              <Link to="/" className="admin-link" onClick={handleAdminNavClick}>
                <span>Boutique</span>
              </Link>

              {/* Products Dropdown */}
              <div className="admin-dropdown-wrapper">
                <button
                  className="admin-link admin-dropdown-trigger"
                  onClick={() => {
                    setShowProductsDropdown(!showProductsDropdown);
                    setShowCategoriesAdminDropdown(false);
                    setShowUsersDropdown(false);
                    setShowOrdersDropdown(false);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <span>Produits</span>
                  <ChevronDown size={16} className={`admin-chevron ${showProductsDropdown ? 'rotated' : ''}`} />
                </button>
                {showProductsDropdown && (
                  <div className="admin-dropdown">
                    <Link to="/admin/add-product" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Ajouter un produit
                    </Link>
                    <Link to="/admin/products" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Tous les produits
                    </Link>
                  </div>
                )}
              </div>

              {/* Categories Dropdown */}
              <div className="admin-dropdown-wrapper">
                <button
                  className="admin-link admin-dropdown-trigger"
                  onClick={() => {
                    setShowCategoriesAdminDropdown(!showCategoriesAdminDropdown);
                    setShowProductsDropdown(false);
                    setShowUsersDropdown(false);
                    setShowOrdersDropdown(false);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <span>Categories</span>
                  <ChevronDown size={16} className={`admin-chevron ${showCategoriesAdminDropdown ? 'rotated' : ''}`} />
                </button>
                {showCategoriesAdminDropdown && (
                  <div className="admin-dropdown">
                    <Link to="/admin/add-category" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Ajouter une categorie
                    </Link>
                    <Link to="/admin/categories" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Toutes les categories
                    </Link>
                  </div>
                )}
              </div>

              {/* Users Dropdown */}
              <div className="admin-dropdown-wrapper">
                <button
                  className="admin-link admin-dropdown-trigger"
                  onClick={() => {
                    setShowUsersDropdown(!showUsersDropdown);
                    setShowProductsDropdown(false);
                    setShowCategoriesAdminDropdown(false);
                    setShowOrdersDropdown(false);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <span>Utilisateurs</span>
                  <ChevronDown size={16} className={`admin-chevron ${showUsersDropdown ? 'rotated' : ''}`} />
                </button>
                {showUsersDropdown && (
                  <div className="admin-dropdown">
                    <Link to="/admin/users" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Tous les utilisateurs
                    </Link>
                  </div>
                )}
              </div>

              {/* Orders Dropdown */}
              <div className="admin-dropdown-wrapper">
                <button
                  className="admin-link admin-dropdown-trigger"
                  onClick={() => {
                    setShowOrdersDropdown(!showOrdersDropdown);
                    setShowProductsDropdown(false);
                    setShowCategoriesAdminDropdown(false);
                    setShowUsersDropdown(false);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <span>Commandes</span>
                  <ChevronDown size={16} className={`admin-chevron ${showOrdersDropdown ? 'rotated' : ''}`} />
                </button>
                {showOrdersDropdown && (
                  <div className="admin-dropdown">
                    <Link to="/admin/orders" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Toutes les commandes
                    </Link>
                  </div>
                )}
              </div>

              {/* Settings Dropdown */}
              <div className="admin-dropdown-wrapper">
                <button
                  className="admin-link admin-dropdown-trigger"
                  onClick={() => {
                    setShowSettingsDropdown(!showSettingsDropdown);
                    setShowProductsDropdown(false);
                    setShowCategoriesAdminDropdown(false);
                    setShowUsersDropdown(false);
                    setShowOrdersDropdown(false);
                  }}
                >
                  <span>Parametres</span>
                  <ChevronDown size={16} className={`admin-chevron ${showSettingsDropdown ? 'rotated' : ''}`} />
                </button>
                {showSettingsDropdown && (
                  <div className="admin-dropdown">
                    <Link to="/admin/promos" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Codes promo
                    </Link>
                    <Link to="/admin/delivery" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Livraison
                    </Link>
                    <Link to="/admin/banners" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Bannieres
                    </Link>
                    <Link to="/admin/meta-pixel" className="admin-dropdown-item" onClick={handleAdminNavClick}>
                      Meta Pixel
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/admin/reviews" className="admin-link" onClick={handleAdminNavClick}>
                <span>Avis</span>
              </Link>

              <Link to="/admin/chats" className="admin-link" onClick={handleAdminNavClick}>
                <span>Chats</span>
              </Link>

              <Link to="/admin" className="admin-link" onClick={handleAdminNavClick}>
                <span>Tableau de bord</span>
              </Link>
            </div>

            <div className="admin-actions-desktop">
              <AdminNotifications
                notifications={notifications}
                loadingNotifications={loadingNotifications}
                onMarkAllRead={markAllAsRead}
                onClearSection={clearSection}
              />
              
              <button onClick={logout} className="admin-logout-button">
                <LogOut size={18} />
                <span>Deconnexion</span>
              </button>
            </div>

            <button 
              onClick={logout} 
              className="admin-logout-button admin-logout-mobile"
            >
              <LogOut size={18} />
              <span>Deconnexion</span>
            </button>
          </div>
        </div>
      </nav>
    );
  }

  // Standard Shop Header
  return (
    <header className="header">
      <div className="header-top">
        <div className="header-container">
          <Link to="/" className="header-logo">
            <img
              src="https://res.cloudinary.com/drj2hrug3/image/upload/v1761774280/Picsart_25-10-29_22-42-31-059_kuvztc.png"
              alt="Boutique Reyza"
              decoding="async"
            />
          </Link>

          <div className="header-search-desktop">
            <SearchBar />
          </div>

          <div className="header-actions">
            <button 
              onClick={isCartOpen ? closeCart : openCart} 
              className="header-icon-button cart-button"
              aria-label="Ouvrir le panier"
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && <span className="notification-badge cart-badge">{cartCount}</span>}
              <span className="icon-label">Panier</span>
            </button>

            {user ? (
              <>
                <Link to="/profile" className="header-icon-button">
                  <User size={22} />
                  <span className="icon-label">Profil</span>
                </Link>
                <button onClick={logout} className="header-logout-btn">
                  <LogOut size={20} />
                  <span>Deconnexion</span>
                </button>
              </>
            ) : (
              <div className="header-auth-buttons">
                <Link to="/login" className="header-login-btn">Connexion</Link>
                <Link to="/register" className="header-register-btn">Inscription</Link>
              </div>
            )}

            <button 
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Basculer le menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <div className="header-bottom">
        <div className="header-container">
          <nav className={`header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <Link to="/" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
              Accueil
            </Link>

            <div className="categories-container">
              <div className="categories-trigger-wrapper">
                <button
                  onClick={handleCategoriesTextClick}
                  className="nav-link categories-text"
                >
                  Categories
                </button>
                <button
                  onClick={toggleCategoriesDropdown}
                  className="categories-trigger"
                  aria-label="Basculer les categories"
                  aria-expanded={showCategoriesDropdown}
                >
                  <ChevronDown 
                    size={16} 
                    className={`categories-chevron ${showCategoriesDropdown ? 'rotated' : ''}`} 
                  />
                </button>
              </div>

              {showCategoriesDropdown && (
                <div className="categories-dropdown" role="menu">
                  {loadingCategories ? (
                    <div className="dropdown-loading">Chargement des categories...</div>
                  ) : categories.length === 0 ? (
                    <div className="dropdown-empty">Aucune categorie disponible</div>
                  ) : (
                    <div className="categories-list">
                      {categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/category/${category.slug || category.id}`}
                          className="category-item"
                          onClick={() => {
                            setShowCategoriesDropdown(false);
                            setMobileMenuOpen(false);
                          }}
                          role="menuitem"
                        >
                          <div className="category-image-wrapper">
                            <img 
                              src={category.imageUrl || '/placeholder-image.jpg'} 
                              alt={category.name}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <span className="category-name">{category.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <Link to="/shop-ai" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
              Acheter avec l'IA
            </Link>

            {user && (
              <div className="header-user-info">
                <span className="user-greeting">Bienvenue, {user.firstName}</span>
              </div>
            )}
          </nav>

          <div className="header-search-mobile">
            <SearchBar />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
